(ns storrito.editor.sticker-design)

;; Concept:
;;
;; Contains tools to create Instagram stickers with different designs
;; / colors.

;; Derive the origin from the script's own src so that images, fonts, etc.
;; can be loaded with absolute URLs when the page is hosted on a different
;; domain. document.currentScript is only available during initial script
;; execution, so we capture it once at load time. When running inside the
;; editor via shadow-cljs, currentScript is null and this falls back to nil,
;; keeping URLs relative (correct for same-origin).
#?(:cljs
   (def script-origin
     (when-let [src (some-> js/document.currentScript .-src)]
       (try (.-origin (js/URL. src))
            (catch :default _ nil)))))

#?(:cljs
   (defn load-img!
     [url]
     (let [img (new js/Image)]
       (set! img.src (str (or script-origin "") url))
       img)))

#?(:cljs
   (def icon-map
     {"/img/link_white.svg" (load-img! "/img/link_white.svg")
      "/img/location-v2-rainbow.svg" (load-img! "/img/location-v2-rainbow.svg")
      "/img/location-v2-orange.svg" (load-img! "/img/location-v2-orange.svg")
      "/img/location-v2-black.svg" (load-img! "/img/location-v2-black.svg")
      "/img/location-v2-white.svg" (load-img! "/img/location-v2-white.svg")
      "/img/location-v2.svg" (load-img! "/img/location-v2.svg")
      "/img/music-v2-white.svg" (load-img! "/img/music-v2-white.svg")
      "/img/link-v2-rainbow.svg" (load-img! "/img/link-v2-rainbow.svg")
      "/img/link-v2-black.svg" (load-img! "/img/link-v2-black.svg")
      "/img/link-v2-white.svg" (load-img! "/img/link-v2-white.svg")
      "/img/link-v2.svg" (load-img! "/img/link-v2.svg")
      "/img/link.svg" (load-img! "/img/link.svg")
      "/img/link_black.svg" (load-img! "/img/link_black.svg")
      }))

(def poll-v2-colors
  {"black" "#262626"
   "pink" "#FE0169"
   "lavender" "#D402C6"
   "purple" "#7536FA"
   "orange" "#FF7A00"
   "green" "#1DD04F"
   "blue" "#0294F6"})

(def question-colors
  {"white" "#ffffff"
   "black" "#262626"
   "pink" "#FE0169"
   "lavender" "#D402C6"
   "purple" "#7536FA"
   "orange" "#FF7A00"
   "green" "#1DD04F"
   "blue" "#0294F6"
  })

(def countdown-colors
  {"black"  {:start_background_color "#262626" :end_background_color "#262626"
             :digit_color "#262626" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   "white"  {:start_background_color "#ffffff" :end_background_color "#ffffff"
             :digit_color "#262626" :digit_card_color "#efefefff" :text_color "#262626"}
   "purple" {:start_background_color "#a307ba" :end_background_color "#a307ba"
             :digit_color "#a307ba" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   "red"    {:start_background_color "#ed4956" :end_background_color "#ed4956"
             :digit_color "#ed4956" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   "orange" {:start_background_color "#fd8d32" :end_background_color "#fd8d32"
             :digit_color "#fd8d32" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   "yellow" {:start_background_color "#fdcb5c" :end_background_color "#fdcb5c"
             :digit_color "#d2a031" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   "green"  {:start_background_color "#70c050" :end_background_color "#70c050"
             :digit_color "#70c050" :digit_card_color "#ffffffcc" :text_color "#ffffff"}
   "blue"   {:start_background_color "#3897f0" :end_background_color "#3897f0"
             :digit_color "#3897f0" :digit_card_color "#ffffffcc" :text_color "#ffffff"}})

(def countdown-default-color (get countdown-colors "black"))

(def new-common-designs
  [
   ;; Opaque gray
   {:design-name "gray"
    :text-props {:fontFamily "Instagram Sans Condensed"
                 :fontSize  120
                 :top 5
                 :fill "#ffffff"}
    :padding-x 80
    :padding-y 40
    :rx 20
    :ry 20
    :opacity 0.5
    :bg-color "#cccccc"}

   ;; Rainbow
   {:design-name "rainbow"
    :text-props {:fontFamily "Instagram Sans Condensed"
                 :fontSize  120
                 :top 5}
    :gradient {"0.0" "#f71401"
               "0.25" "#ffb808"
               "0.5" "#7ad933"
               "0.75" "#019dea"
               "1" "#5558f5"}
    :padding-x 80
    :padding-y 40
    :rx 20
    :ry 20
    :opacity 1.0}])

(def default-hashtag-design
  {:design-name "default"
   :text-props {:fontFamily "Instagram Sans Condensed"
                :fontSize 120
                :top 5
                :fill "#f10663"}
   :padding-x 80
   :padding-y 40
   :rx 20
   :ry 20
   :opacity 1.0})

(def new-hashtag-designs
  (cons
    default-hashtag-design
    new-common-designs))

(def default-mention-design
  {:design-name "default"
   :text-props {:fontFamily "Instagram Sans Condensed"
                :fontSize 120
                :top 5
                :fill "#ff7b00"}
   :padding-x 80
   :padding-y 40
   :rx 20
   :ry 20
   :opacity 1.0})

(def new-mention-designs
  (cons
    default-mention-design
    new-common-designs))

(def all-new-designs
  (concat [default-hashtag-design
           default-mention-design]
          new-common-designs))

(def designs
  {:hashtag-standard {:text-props {:fontFamily "Instagram Sans Condensed"
                                   :fontSize 120
                                   :top 5}
                      :padding-x 80
                      :padding-y 40
                      :rx 20
                      :ry 20
                      :gradient {"0" "#f58c5c"
                                 "1" "#ff4f99"}
                      :opacity 1.0}
   :usertag-standard {:text-props {:fontFamily "Instagram Sans Condensed"
                                   :fontSize 120
                                   :top 5}
                      :padding-x 80
                      :padding-y 40
                      :rx 20
                      :ry 20
                      :gradient {"0" "#df6744"
                                 "1" "#ebe550"}
                      :opacity 1.0}
   :location-standard {:text-props {:fontFamily "Instagram Sans Condensed"
                                    :fontSize 120
                                    :top 5}
                       :padding-x 80
                       :padding-y 40
                       :rx 20
                       :ry 20
                       :gradient {"0" "#a100b2"
                                  "1" "#2701ef"}
                       :opacity 1.0}
   :storrito-standard {:text-props {:fontFamily "Instagram Sans Condensed"
                                    :fontSize 120
                                    :top 5}
                       :padding-x 80
                       :padding-y 40
                       :rx 20
                       :ry 20
                       :opacity 1.0
                       :gradient {"0" "#00baff"
                                  "1" "#fe32ea"}}
   :rainbow {:text-props {:fontFamily "Instagram Sans Condensed"
                          :fontSize 120
                          :top 5}
             :padding-x 80
             :padding-y 40
             :rx 20
             :ry 20
             :opacity 1.0
             :gradient {"0" "#fe6440"
                        "0.25" "#ffbf5b"
                        "0.5" "#85c25c"
                        "0.75" "#2da2de"
                        "1" "#9220b5"}}
   :gray-bg {:text-props {:fontFamily "Instagram Sans Condensed"
                          :fontSize 120
                          :top 5}
             :padding-x 80
             :padding-y 40
             :rx 20
             :ry 20
             :icon-props {:src "/img/link_white.svg"}
             :opacity 0.5
             :gradient {"0" "#ffffff"
                        "1" "#ffffff"}
             :bg-color "#cccccc"}
   :gray-fg {:text-props {:fontFamily "Instagram Sans Condensed"
                          :fontSize 120
                          :top 5}
             :padding-x 80
             :padding-y 40
             :rx 20
             :ry 20
             :icon-props {:src "/img/link_black.svg"}
             :opacity 1.0
             :gradient {"0" "#000000"
                        "1" "#000000"}}
   :poll-standard {:text-props {:fontFamily "Instagram Sans Condensed"
                                :fontSize 120
                                :top 5}
                   :padding-x 80
                   :padding-y 40
                   :rx 20
                   :ry 20
                   :opacity 1.0
                   :gradient {"0" "#00baff"
                              "1" "#3acc38"}}
   ;; TODO: Find the real colors for countdown
   :countdown-standard {:text-props {:fontFamily "Instagram Sans Condensed"
                                     :fontSize 120
                                     :top 5}
                        :padding-x 80
                        :padding-y 40
                        :rx 20
                        :ry 20
                        :opacity 1.0
                        :gradient {"0" "#cc33ff"
                                   "1" "#66ccff"}}
   :questions-standard {:text-props {:fontFamily "Instagram Sans Condensed"
                                     :fontSize 120
                                     :top 5}
                        :gradient {"0" "#bc00af"
                                   "0.3" "#f60008"
                                   "1" "#f60008"}
                        :opacity 1.0
                        :padding-x 80
                        :padding-y 40
                        :rx 20
                        :ry 20}
   :quiz-standard {:text-props {:fontFamily "Instagram Sans Condensed"
                                :fontSize 120
                                :top 5}
                   :gradient {"0" "#262626"
                              "1" "#262626"}
                   :padding-x 80
                   :padding-y 40
                   :rx 20
                   :ry 20}
   :quiz-gradient-1 {:text-props {:fontFamily "Instagram Sans Condensed"
                                  :fontSize 120
                                  :top 5}
                     :gradient {"0" "#ffffff"
                                "1" "#ffffff"}
                     :padding-x 80
                     :padding-y 40
                     :rx 20
                     :ry 20
                     :bg-color "#cccccc0c"}
   :link-standard {:text-props {:fontFamily "Instagram Sans Condensed"
                                :fontSize 120
                                :top 5}
                   :icon-props {:src "/img/link.svg"}
                   :gradient {"0" "#2cbdec"
                              "1" "#2f2e96"}
                   :opacity 1.0
                   :padding-x 80
                   :padding-y 40
                   :rx 20
                   :ry 20}})

(defn find-design-by-name [designs design-name default-design]
  (or (some #(when (= (:design-name %) design-name) %) designs)
      default-design))

(def jp-regex #"[\u3040-\u30FF\u4E00-\u9FFF]+")

(defn japanese? [s]
  (boolean (re-find jp-regex s)))

(defn check-font
  [attrs text]
  (if (and (string? text)
           (japanese? text))
    (assoc-in attrs [:text-props :fontFamily] "Noto Sans CJK JP")
    attrs))

(comment

  (check-font {:text-props {:fontFamily "Instagram Sans Condensed"
                            :fontSize 120
                            :top 5
                            :left 40}
               :padding-x 180
               :padding-y 40
               :rx 20
               :ry 20}
              "購入する" #_ "POMMES"
              )
  )

(def default-link-design
  {:icon-props {:src "/img/link-v2.svg"
                :scaleX 0.2
                :scaleY 0.2
                :left 30}
   :text-props {:fontFamily "Instagram Sans Condensed"
                :fontSize 120
                :top 5
                :left 40
                :fill "#0292ed"}
   :padding-x 180
   :padding-y 40
   :rx 20
   :ry 20
   :opacity 1.0})

(def new-link-designs
  (let [common {:text-props {:fontFamily "Instagram Sans Condensed"
                             :fontSize 120
                             :top 5
                             :left 40}
                :padding-x 180
                :padding-y 40
                :rx 20
                :ry 20}]
    (into []
          (map
            (fn [d]
              (merge-with merge
                          common d))
            [{:design-name "default"
              :icon-props {:src "/img/link-v2.svg"
                           :scaleX 0.2
                           :scaleY 0.2
                           :left 30}
              :text-props {:fill "#0292ed"}
              :opacity 1.0}
             {:design-name "gray"
              :icon-props {:src "/img/link-v2-white.svg"
                           :scaleX 0.2
                           :scaleY 0.2
                           :left 30}
              :text-props {:fill "#ffffff"}
              :opacity 0.5
              :bg-color "#cccccc"}
             {:design-name "black"
              :icon-props {:src "/img/link-v2-black.svg"
                           :scaleX 0.2
                           :scaleY 0.2
                           :left 30}
              :text-props {:fill "#000000"}
              :opacity 1.0}
             {:design-name "rainbow"
              :icon-props {:src "/img/link-v2-rainbow.svg"
                           :scaleX 0.2
                           :scaleY 0.2
                           :left 30}
              :text-props {:fill "#000000"}
              :opacity 1.0}]))))

(def invisible-tag-design
  {:text-props {:fontFamily "Instagram Sans Condensed"
                :fontSize 120
                :top 5
                :fill "#ffffff"}
   :padding-x 80
   :padding-y 40
   :rx 20
   :ry 20
   :opacity 0.2
   :bg-color "#cccccc"})

(def invisible-link-design
  {:icon-props {:src "/img/link-v2-white.svg"
                :scaleX 0.2
                :scaleY 0.2
                :left 20}
   :text-props {:fontFamily "Instagram Sans Condensed"
                :fill "#ffffff"
                :fontSize 120
                :top 5
                :left 40}
   :opacity 0.2
   :bg-color "#cccccc"
   :padding-x 180
   :padding-y 40
   :rx 20
   :ry 20})

(def invisible-location-design
  {:icon-props {:src "/img/location-v2-white.svg"
                :scaleX 0.2
                :scaleY 0.2
                :left 20}
   :text-props {:fontFamily "Instagram Sans Condensed"
                :fill "#ffffff"
                :fontSize 120
                :top 5
                :left 40}
   :opacity 0.2
   :bg-color "#cccccc"
   :padding-x 150
   :padding-y 40
   :rx 20
   :ry 20})

(def invisible-music-design
  {:icon-props {:src "/img/music-v2-white.svg"
                :scaleX 0.2
                :scaleY 0.2
                :left 20}
   :text-props {:fontFamily "Instagram Sans Condensed"
                :fill "#ffffff"
                :fontSize 120
                :top 5
                :left 40}
   :opacity 0.2
   :bg-color "#cccccc"
   :padding-x 150
   :padding-y 40
   :rx 20
   :ry 20})

(def default-location-design
  {:icon-props {:src "/img/location-v2.svg"
                :scaleX 0.2
                :scaleY 0.2
                :left 20}
   :text-props {:fontFamily "Instagram Sans Condensed"
                :fontSize 120
                :top 5
                :left 30
                :fill "#7437fa"}
   :padding-x 150
   :padding-y 40
   :rx 20
   :ry 20
   :opacity 1.0})

(def new-location-designs
  (let [common {:text-props {:fontFamily "Instagram Sans Condensed"
                             :fontSize 120
                             :top 5
                             :left 30}
                :padding-x 150
                :padding-y 40
                :rx 20
                :ry 20}]
    (into []
          (map
            (fn [d]
              (merge-with merge
                          common
                          d))
            [{:design-name "default"
              :icon-props {:src "/img/location-v2.svg"
                           :scaleX 0.2
                           :scaleY 0.2
                           :left 20}
              :text-props {:fill "#7437fa"}
              :opacity 1.0}
             {:design-name "gray"
              :icon-props {:src "/img/location-v2-white.svg"
                           :scaleX 0.2
                           :scaleY 0.2
                           :left 20}
              :text-props {:fill "#ffffff"}
              :opacity 0.5
              :bg-color "#cccccc"}
             {:design-name "black"
              :icon-props {:src "/img/location-v2-black.svg"
                           :scaleX 0.2
                           :scaleY 0.2
                           :left 20}
              :text-props {:fill "#000000"}
              :opacity 1.0}
             {:design-name "rainbow"
              :icon-props {:src "/img/location-v2-rainbow.svg"
                           :scaleX 0.2
                           :scaleY 0.2
                           :left 20}
              :opacity 1.0
              :gradient {"0" "#ff7b00"
                         "0.25" "#efd200"
                         "0.5" "#13d518"
                         "0.75" "#12c5ca"
                         "1" "#0041eb"}}
             {:design-name "orange"
              :icon-props {:src "/img/location-v2-orange.svg"
                           :scaleX 0.2
                           :scaleY 0.2
                           :left 20}
              :text-props {:fill "#000000"}
              :opacity 1.0}]))))
